import styles from './ArticlePurchasePriceWithHistoryCell.module.scss';

import { ClickAwayListener, Popper, Stack, Typography } from '@mui/material';
import React, { useRef, useState } from 'react';

import { useSinglePriceWithContext } from '@work4all/data';

import { Article } from '@work4all/models/lib/Classes/Article.entity';
import { Position } from '@work4all/models/lib/Classes/Position.entity';
import { ArticlePurchaseInformationObjectType } from '@work4all/models/lib/Enums/ArticlePurchaseInformationObjectType.enum';

import { formatNumberAsCurrency } from '@work4all/utils';

import { SupplierPricePicker } from '../../../../../../../../../../../components/data-tables/offers-table/components/SupplierPricePicker/SupplierPricePicker';

interface ArticleOptions {
  purchasePrice?: number;
  update?: {
    position: Position;
  };
}

export type IArticlePurchasePriceCellProps = {
  row?: {
    original: Article;
  };
  column?: {
    id: string;
    cellParams: {
      source?: 'table' | 'positions';
      onSelected?: (article: Article, options?: ArticleOptions) => void;
    };
  };
};

export const ArticlePurchasePriceWithHistoryCell: React.FC<
  IArticlePurchasePriceCellProps
> = (props) => {
  const { row } = props;

  const options = useSinglePriceWithContext();

  const standardSupplierId =
    row?.original.standardSupplierId ?? row?.original.defaultSupplier?.id;
  const purchasePriceList = row?.original.articlePrices?.purchasePriceList;

  const standardPurchasePrice = purchasePriceList?.find(
    (x) => x.supplier?.id === standardSupplierId
  );
  const standardPurchasePriceValue = standardPurchasePrice?.purchasePrice;
  const defaultPriceValue =
    standardPurchasePriceValue ?? purchasePriceList?.[0]?.purchasePrice ?? null;

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const open = Boolean(anchorEl);
  const id = open ? 'supplier-tooltip' : undefined;

  const handleSelection = (selectedRow) => {
    props.column?.cellParams.onSelected?.(row?.original, {
      purchasePrice: selectedRow.objectId,
      update:
        selectedRow.purchaseInfoType !==
        ArticlePurchaseInformationObjectType.ARTICLE_PURCHASE_PRICE
          ? {
              position: {
                supplierId: selectedRow.supplier?.id,
                purchasePrice: selectedRow.price,
              },
            }
          : undefined,
    });
    setAnchorEl(null);
  };

  const clickAwayBlocker = useRef(false);
  return (
    <Stack
      flexDirection="row"
      alignItems="center"
      justifyContent="flex-end"
      gap={0.5}
    >
      <Typography variant="body2">
        {defaultPriceValue
          ? formatNumberAsCurrency(defaultPriceValue, options)
          : ''}
      </Typography>
      {purchasePriceList?.length > 1 && (
        <Typography
          variant="body2"
          className={styles.secondaryInfo}
          aria-describedby={id}
          onClick={(event) => {
            clickAwayBlocker.current = true;
            setAnchorEl(event.currentTarget as HTMLElement);
            setTimeout(() => {
              clickAwayBlocker.current = false;
            }, 300);
          }}
        >
          {`(+${purchasePriceList.length - 1})`}
        </Typography>
      )}

      <ClickAwayListener
        onClickAway={() => {
          if (anchorEl && !clickAwayBlocker.current) setAnchorEl(null);
        }}
      >
        <Popper
          id={id}
          open={open}
          anchorEl={anchorEl}
          placement="bottom-start"
          modifiers={[
            {
              name: 'offset',
              options: {
                offset: [0, 8],
              },
            },
          ]}
          onMouseEnter={() => {
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
          }}
          className={styles.popper}
          style={{
            zIndex: 9999,
            backgroundColor: 'var(--mui-palette-ui1-main)',
          }}
        >
          <SupplierPricePicker
            articleId={row?.original.id}
            value={null}
            onChange={handleSelection}
            onlySuppliersWithPurchasePrice={true}
          />
        </Popper>
      </ClickAwayListener>
    </Stack>
  );
};
